import type React from 'react'
import { useState, useMemo } from 'react'
import { Switch, Popover, message } from 'antd'
import { Horizontal, Vertical, GraviGrid, Texto, GraviButton } from '@gravitate-js/excalibrr'
import { type ProductMappings, useSupplierReconciliation, type SupplierConfig } from '@api/useSupplierReconciliation'
import { useMasterMetadata } from '@api/useMetadata/useMasterMetadata'
import type { SupplierConfigMetadataResponse } from '@api/useMetadata/types'
import { getProductMappingsColumns } from './productMappingsColumns'
import { AddExternalValuePopover } from '../shared/AddExternalValuePopover'
import { PlusOutlined } from '@ant-design/icons'
import type { IFilter } from 'ag-grid-community'
import { gridViewManager } from '@/utils/grid'
import { useUserSettings } from '@/hooks/settings'

const STORAGE_KEY = 'product-mappings-grid'

/**
 * Product Mapping tab
 * Contains product mapping management
 */
export function ProductMappingTab() {
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false)
  const [isAddingExternalProduct, setIsAddingExternalProduct] = useState(false)

  const { updateSetting } = useUserSettings()

  // Get product mapping data and supplier config data
  const { useProductMappingsOverview, useUpsertProductMapping, useConfigOverview } = useSupplierReconciliation()
  const { data: mappingData, isLoading: isMappingLoading } = useProductMappingsOverview()
  const { data: configData } = useConfigOverview()

  // Get metadata options
  const { data: metadata } = useMasterMetadata<SupplierConfigMetadataResponse>('supplier_config')

  // Get update mutation
  const { mutateAsync: updateMapping } = useUpsertProductMapping()

  // Transform product hierarchy into flat list of products
  const products = useMemo(() => {
    if (!metadata?.product_hierarchy) return []
    return Object.values(metadata.product_hierarchy)
      .flat()
      .map((product) => ({
        id: product.id,
        name: product.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [metadata?.product_hierarchy])

  // Handle adding new external product
  async function handleAddExternalProduct(values: {
    source_name: string
    source_id?: string
    counterparty_id?: string
    supplier_id?: string
    product_ids?: string[]
  }) {
    // Find existing mapping for this source_name and supplier_id
    const existingMapping = mappingData?.find(
      (item: ProductMappings) => item.source_name === values.source_name && item.supplier_id === values.supplier_id
    )

    // Get existing product IDs
    let existingProductIds: string[] = []
    if (existingMapping?.products && Array.isArray(existingMapping.products)) {
      existingProductIds = existingMapping.products.map((p: { product_id: string }) => p.product_id).filter(Boolean)
    }

    // Get new product IDs
    const newProductIds = values.product_ids || []

    // Combine existing and new product IDs (remove duplicates)
    const combinedProductIds = [...new Set([...existingProductIds, ...newProductIds])]

    try {
      message.loading('Adding external product...', 0)
      await updateMapping({
        body: {
          supplier_id: values.supplier_id,
          source_name: values.source_name,
          product_ids: combinedProductIds,
        },
      })
      message.destroy()
      message.success('External product added successfully')
    } catch (error: unknown) {
      message.destroy()
      console.error('Failed to add product mapping:', error)
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { status?: number; data?: { detail?: string } } }
        if (apiError.response?.status === 422) {
          const errorDetail = apiError.response?.data?.detail || 'Validation error'
          message.error(`Failed to add product mapping: ${errorDetail}`)
        } else {
          message.error('Failed to add product mapping')
        }
      } else {
        message.error('Failed to add product mapping')
      }
    } finally {
      setIsAddingExternalProduct(false)
    }
  }
  async function upsertProductMapping(data: ProductMappings) {
    let productIds: string[] = []

    // Check if this is edited data from the grid (will have product_names array)
    if (data.product_names && Array.isArray(data.product_names)) {
      // Convert product names to IDs
      productIds = data.product_names
        .map((name) => products.find((p) => p.name === name)?.id)
        .filter(Boolean) as string[]
    }
    // Otherwise, it's original data from API with products array
    else if (data.products && Array.isArray(data.products)) {
      productIds = data.products.map((p: { product_id: string }) => p.product_id).filter(Boolean) as string[]
    }

    try {
      message.loading('Updating product mapping...', 0)
      await updateMapping({
        body: {
          supplier_id: data.supplier_id,
          source_name: data.source_name,
          product_ids: productIds,
        },
      })
      message.destroy()
      message.success('Product mapping updated successfully')
    } catch (error: unknown) {
      message.destroy()
      console.error('Failed to update product mapping:', error)
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { status?: number; data?: { detail?: string } } }
        if (apiError.response?.status === 422) {
          const errorDetail = apiError.response?.data?.detail || 'Validation error'
          message.error(`Failed to update product mapping: ${errorDetail}`)
        } else {
          message.error('Failed to update product mapping')
        }
      } else {
        message.error('Failed to update product mapping')
      }
      throw error // Re-throw to prevent grid from thinking it succeeded
    }
  }

  // Get column definitions
  const columnDefs = useMemo(() => getProductMappingsColumns(), [])

  const filteredItems = useMemo(() => {
    if (!mappingData) return undefined
    if (showUnmappedOnly) {
      return mappingData.filter((item: ProductMappings) => {
        // Check all possible data structures for unmapped items
        if (Array.isArray(item.products) && item.products.length > 0) {
          return false // Has mappings
        }
        if (Array.isArray(item.product_names) && item.product_names.length > 0) {
          return false // Has mappings
        }
        if (item.product_name) {
          return false // Has mapping
        }
        return true // No mappings found
      })
    }
    return mappingData
  }, [mappingData, showUnmappedOnly])

  return (
    <Vertical gap={16}>
      <GraviGrid<ProductMappings, IFilter>
        rowData={filteredItems}
        columnDefs={columnDefs}
        storageKey={STORAGE_KEY}
        loading={isMappingLoading}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
        controlBarProps={{
          title: 'Product Mappings',
          actionButtons: (
            <Horizontal gap={8} alignItems='center'>
              <Texto>Show Unmapped Only</Texto>
              <Switch
                data-testid='show-unmapped-only-switch'
                checked={showUnmappedOnly}
                onChange={setShowUnmappedOnly}
              />
              <Popover
                trigger='click'
                placement='bottomRight'
                open={isAddingExternalProduct}
                onOpenChange={setIsAddingExternalProduct}
                content={
                  <AddExternalValuePopover
                    type='product'
                    onSubmit={handleAddExternalProduct}
                    supplierConfigs={configData?.rows}
                    productOptions={products}
                  />
                }
              >
                <GraviButton icon={<PlusOutlined />} buttonText='Add External Product' theme2 />
              </Popover>
            </Horizontal>
          ),
          rowCount: filteredItems?.length || 0,
        }}
        agPropOverrides={{
          groupDefaultExpanded: -1,
          rowSelection: 'single',
          getRowId: (params) => {
            const baseId = params.data.source_name + params.data.supplier_id
            // Create unique ID by including all mapped product IDs
            if (Array.isArray(params.data.products) && params.data.products.length > 0) {
              const productIds = params.data.products
                .map((p: { product_id: string }) => p.product_id)
                .sort()
                .join(',')
              return baseId + '_products_' + productIds
            }
            // Fallback for unmapped items
            return baseId + '_unmapped'
          },
          context: {
            productOptions: products,
          },
          onCellEditingStopped: (params) => {
            // Only trigger API call if value actually changed
            if (!params.valueChanged) return

            // Use oldValue and newValue directly from AG Grid for accurate comparison
            const normalizeValue = (value: unknown): string[] => {
              if (!value) return []
              if (Array.isArray(value)) {
                return value.filter((name) => name && name !== 'No Mapping').sort()
              }
              if (typeof value === 'string' && value !== 'No Mapping') {
                return [value]
              }
              return []
            }

            const oldValues = normalizeValue(params.oldValue)
            const newValues = normalizeValue(params.newValue)

            // Deep comparison: check if arrays are actually different
            const arraysEqual =
              oldValues.length === newValues.length && oldValues.every((name, index) => name === newValues[index])

            if (arraysEqual) {
              console.log('Product mapping: No real change detected', { oldValues, newValues })
              return // No real change in product mappings
            }

            console.log('Product mapping: Change detected, updating...', { oldValues, newValues })
            upsertProductMapping(params.data)
          },
        }}
      />
    </Vertical>
  )
}
